import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../api";
import AuthLayout from "../components/AuthLayout";
import { Button, Input, Label } from "../components/ui/Primitives";

export default function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      // Phase 34d — trim email (paste/autocomplete often adds trailing whitespace).
      // Password is sent verbatim as-is; do NOT trim or transform — special chars
      // like `!`, `$`, `&` must pass through unchanged.
      const user = await login(email.trim(), String(password));
      const dest = location.state?.from || (user.role === "super_admin" ? "/admin" : "/dashboard");
      navigate(dest, { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2));
      toast.error(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title={t("auth.login_title")}
      subtitle={t("auth.login_subtitle")}
      footer={
        <span>
          {t("auth.no_account")}{" "}
          <Link to="/pricing" data-testid="link-to-pricing" className="text-brand hover:text-brand-hover font-medium">
            {t("auth.choose_plan")}
          </Link>
        </span>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
        <div>
          <Label htmlFor="email">{t("common.email")}</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@firma.at"
            autoComplete="email"
            required
            data-testid="login-email-input"
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label htmlFor="password" className="!mb-0">{t("common.password")}</Label>
            <Link to="/forgot-password" data-testid="link-forgot" className="text-[11px] text-brand hover:text-brand-hover">
              {t("auth.forgot_link")}
            </Link>
          </div>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            data-testid="login-password-input"
          />
        </div>

        {err && (
          <div data-testid="login-error" className="text-sm text-err bg-err/10 border border-err/30 rounded-md px-3 py-2">
            {err}
          </div>
        )}

        <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
          {loading ? t("common.loading") : t("auth.submit_login")}
        </Button>

        <div className="pt-1 border-t border-white/5 mt-4 text-center">
          <Link to="/pricing" data-testid="login-cta-pricing" className="inline-flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium text-brand hover:text-brand-hover hover:bg-brand/5 transition-colors">
            {t("auth.start_now", "Jetzt starten →")}
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}
